import { Injectable, TemplateRef } from '@angular/core';
import { Event } from '@angular/router';
import { Observable } from 'rxjs';

import { Store } from '@ngrx/store';
import { UtilService } from '../services/util.service';

import { ContextModal, ModalConfig } from '@app/core/models/modal.model';
import { ModalService } from '@app/domain/services/modal.service';
import * as appActions from '@store/app/app.actions';
import * as appSelectors from '@store/app/app.selectors';
import { map } from 'rxjs/operators';
import {
  IAppSession,
  IExtraData,
  IFingerPrint,
  ILargeContentConfig,
  IPutRecord,
  ITransactions,
} from '../core/models/app.model';
import { AppAPI } from '../data/mo-api/app.api';
import { GoogleAnalyticsService } from '../services/google-analytics.service';
import { UIService } from '../services/ui.service';
import { ICustomerValidate } from '../core/models/customer.model';
import { IErrorComponentData } from '../core/models/error.model';

@Injectable({ providedIn: 'root' })
export class AppFacade {
  public currentRoute$: Observable<string>;
  public currentFlow$: Observable<string>;
  public step$: Observable<number>;
  public largeContentConfig$: Observable<ILargeContentConfig>;
  public isLoading$: Observable<boolean>;
  public session$: Observable<IAppSession>;
  public transactions$: Observable<ITransactions>;
  public error$: Observable<boolean>;
  public openMenu$: Observable<boolean>;
  public modalShow$: Observable<boolean>;
  public transactionStatus$: Observable<string>;
  public validation$: Observable<ICustomerValidate>;
  public redirectUrl$: Observable<string>;

  constructor(
    private _utilService: UtilService,
    private _store: Store,
    private _uiService: UIService,
    private _appApi: AppAPI,
    private _gaServices: GoogleAnalyticsService,
    private _modalService: ModalService
  ) {
    this.currentRoute$ = this._store.select(appSelectors.selectCurrentRoute);
    this.currentFlow$ = this._store.select(appSelectors.selectCurrentFlow);
    this.step$ = this._store.select(appSelectors.selectStep);
    this.largeContentConfig$ = this._store.select(appSelectors.selectLargeContentConfig);
    this.isLoading$ = this._store.select(appSelectors.selectIsLoading);
    this.session$ = this._store.select(appSelectors.selectSession);
    this.transactions$ = this._store.select(appSelectors.selectTransactions);
    this.error$ = this._store.select(appSelectors.selectError);
    this.openMenu$ = this._store.select(appSelectors.selectHandleMenu);
    this.modalShow$ = this._store.select(appSelectors.selectModalShow);
    this.validation$ = this._store.select(appSelectors.selectValidation);
    this.redirectUrl$ = this._store.select(appSelectors.selectRedirectUrl);
    this.transactionStatus$ = this._store
      .select(appSelectors.selectTransactions)
      .pipe(map(({ status }) => status));
  }

  public getTransactions(transactionToken: string): Observable<ITransactions> {
    this._store.dispatch(appActions.getTransactions());
    return this._appApi.getTransactions(transactionToken);
  }

  public getTransactionsWithOutId(): void {
    this._store.dispatch(appActions.getTransactionsWithOutId());
  }

  public setTransactions(transactions: ITransactions) {
    return this._store.dispatch(appActions.setTransactions({ transactions }));
  }
  public setDataFingerPint(data: IFingerPrint) {
    return this._store.dispatch(appActions.setDataFingerPrint({ data }));
  }
  public errorTransactions() {
    return this._store.dispatch(appActions.errorTransactions());
  }

  public getTransactionsMock() {
    return this._store.dispatch(appActions.getTransactions());
  }

  public getUrlByChangeEvent(): Observable<Event> {
    return this._utilService.getUrlByChangeEvent();
  }

  public showSnackBar(message: string, type: string = 'warning') {
    this._uiService.showSnackbar(message, type);
  }

  public hideSplash() {
    this._utilService.hideSplash();
  }

  public getCurrentRoute(currentRoute: string) {
    this._store.dispatch(appActions.getCurrentRoute({ currentRoute }));
  }

  public goToStepBack() {
    this._store.dispatch(appActions.stepBack());
  }

  public goToNextStep() {
    this._store.dispatch(appActions.nextStep());
  }

  public setNewStep(step: number) {
    this._store.dispatch(appActions.setNewStep({ step }));
  }

  public setCurrentFlow(currentFlow: string) {
    this._store.dispatch(appActions.setCurrentFlow({ currentFlow }));
  }

  public setNextStepConfig(step: number, currentFlow: string) {
    this.setNewStep(step);
    this.setCurrentFlow(currentFlow);
  }

  public setLargeContentConfig(largeContentConfig: ILargeContentConfig) {
    this._store.dispatch(appActions.setIsLargeContentConfig({ largeContentConfig }));
  }

  public setIsLoading() {
    this._store.dispatch(appActions.setIsLoading({ isLoading: false }));
  }

  public nextStep() {
    this._store.dispatch(appActions.nextStep());
  }

  public setConfigSuccessStep() {
    this.setIsLoading();
    this.nextStep();
  }

  public requestLogin(phone: string, code: string) {
    this._store.dispatch(appActions.requestLogin({ phone, code }));
  }

  public closeSession() {
    this._store.dispatch(appActions.closeSession());
  }

  public killPurchase(description?: string) {
    description = description ?? '';
    this._store.dispatch(appActions.killPurchase({ description }));
  }

  public redirectLoop() {
    this._store.dispatch(appActions.redirectLoop());
  }

  public sendPutRecord(data: IPutRecord, extraData?: IExtraData) {
    this._store.dispatch(appActions.sendPutRecord({ data, extraData }));
  }

  public requestPutRecord(data: IPutRecord) {
    const userInfo = this._utilService.setUserMetricInfo();
    this._appApi.sendPutRecord({ ...data, ...userInfo });
  }

  public sendGoogleMetrics(data: IPutRecord) {
    const { category, action, label } = data;
    this._gaServices.sendEvent(category, action, label);
  }

  public handleMenu() {
    this._store.dispatch(appActions.handleMenu());
  }

  public closeMenu() {
    this._store.dispatch(appActions.closeMenu());
  }

  public openModal(templateRef: TemplateRef<ContextModal>, data?: any): void {
    const modalConfig: ModalConfig = {
      templateRef,
      show: true,
      data,
    };
    this._modalService.setModalConfig = modalConfig;
    this._store.dispatch(appActions.updateModalShow({ modalShow: true }));
  }

  public closeModal(): void {
    this._modalService.setModalConfig = {} as ModalConfig;
    this._store.dispatch(appActions.updateModalShow({ modalShow: false }));
  }

  public killPurchaseWithoutRedirect(
    error?: string,
    is_customer?: boolean,
    componentData?: IErrorComponentData
  ): void {
    this._store.dispatch(
      appActions.killPurchaseWithoutRedirect({ error, is_customer, componentData })
    );
  }

  public setRedirectUrl(
    redirectUrl: string,
    error: string,
    is_customer?: boolean,
    componentData?: IErrorComponentData
  ) {
    this._store.dispatch(
      appActions.setRedirectUrl({ redirectUrl, error, is_customer, componentData })
    );
  }

  public setRedirectUrlSuccessFlow(redirectUrl: string) {
    this._store.dispatch(appActions.setRedirectUrlSuccessFlow({ redirectUrl }));
  }
}
